import { Component, Prop, h } from '@stencil/core';

import { onVehiclesDealerInventory } from '../../../actions/actions.dealer';
import { DealerInfo, DealerInventory } from '../../../interfaces/interface.dealer';
import { trackGetDirectionClick, trackPhoneClick, trackSiteClick } from '../../../tracking/tracking.dealer';
@Component({
  tag: 'dealer-listing-enhancement',
  styleUrl: 'dealer-listing-enhancement.css',
})
export class DealerListingEnhancement {
  @Prop() dealerInfo: DealerInfo | null = null;
  @Prop() index: number = 0;
  @Prop() isClosest: boolean = false;
  @Prop() showDealerList: boolean = false;
  @Prop() dealerInventory: DealerInventory[] = [];

  hasPhoto() {
    return this.dealerInfo?.dealerPhotoUrl?.length > 0;
  }

  renderMarkerWithNumber(number: number) {
    return (
      <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M15.6897 33.314C6.04098 19.3263 4.25 17.8907 4.25 12.75C4.25 5.70835 9.95835 0 17 0C24.0417 0 29.75 5.70835 29.75 12.75C29.75 17.8907 27.959 19.3263 18.3103 33.314C17.6771 34.2287 16.3228 34.2286 15.6897 33.314Z"
          fill="#225EAA"
        />
        <text text-anchor="middle" x="17" y="18" fill="#fff" font-weight="600">
          <tspan>{number}</tspan>
        </text>
      </svg>
    );
  }

  getDealerNameFromInterpretChosen(interpretChosen: string) {
    return interpretChosen.slice(0, interpretChosen.indexOf(' dealer') + 7).replace('A ', '');
  }

  render() {
    const {
      dealershipName,
      distance,
      dealershipPhone,
      ratingInline,
      ratingOverlay,
      specChosen,
      voiCount,
      voiFilterLink,
      voiMake,
      voiModel,
      dealerWebAddress,
      direction,
      interpretChosen,
      dealershipAddress,
      dealershipCity,
      dealershipState,
      dealershipZipCode,
    } = this.dealerInfo || {};

    const { index, isClosest, showDealerList, dealerInventory } = this;

    return (
      <div class="items-center flex @xs:flex-wrap @4xl:flex-nowrap pb-4 mb-4 border-b border-b-clrs-grey border-solid border-t-0 border-l-0 border-r-0" id={`dealer-${index}`}>
        <div class="@xs:w-full @md:w-fit">
          <div class="leading-5">
            <div class="font-bold flex flex-row items-center">
              {index + 1}.<div class="ml-1">{dealershipName}</div>
            </div>
            {specChosen && (
              <div class="@xs:ml-0 @md:ml-5 bg-clrs-light-green border-solid border-1 border-clrs-argo-green rounded-md text-clrs-dark-green font-extrabold py-2 px-3 text-sm mb-2 mt-1 w-fit @md:w-72">
                {specChosen}
              </div>
            )}
            <div class="ml-5 flex flex-row mt-1 mb-2 items-center">
              <div class="flex items-center @xs:mr-4 @md:mr-8 whitespace-nowrap" onClick={() => trackPhoneClick({ dealerIndex: index, dealerPhone: dealershipPhone })}>
                <div class="w-4 h-4 mr-1">
                  <svg-icon icon="phone-dealer" />
                </div>
                <ico-hyperlink href={`tel:${dealershipPhone}`} class="font-bold text-sm">
                  {dealershipPhone}
                </ico-hyperlink>
              </div>
              {dealerWebAddress && (
                <div class="flex items-center whitespace-nowrap" onClick={() => trackSiteClick({ dealerWebsite: dealerWebAddress, dealerIndex: index })}>
                  <svg-icon icon="external-link" class="w-4 h-4 mr-1 flex items-center mt-1" />
                  <ico-hyperlink href={dealerWebAddress} target="_blank" class="font-bold text-sm">
                    Dealer Website
                  </ico-hyperlink>
                </div>
              )}
            </div>
            {ratingInline && (
              <div class="ml-5">
                <dealer-listing-kbb-rating ratingInline={ratingInline} ratingOverlay={ratingOverlay}></dealer-listing-kbb-rating>
              </div>
            )}
            {interpretChosen && (
              <div class="ml-5 flex flex-row mb-2 items-start">
                <svg-icon icon="person" height="16px" width="16px" class="mr-1 mb-1" />
                <div>
                  <strong>{this.getDealerNameFromInterpretChosen(interpretChosen)}</strong>
                  <div class="text-sm">{interpretChosen}</div>
                </div>
              </div>
            )}
            {voiCount !== 0 && (
              <div class="flex items-center ml-5 mb-2">
                <svg-icon icon="car" height="16px" width="16px" class="@xs:mt-2 @md:mt-1" />
                <ico-hyperlink href={voiFilterLink} target="_blank">
                  <strong class="ml-1 text-sm font-bold">
                    {voiCount} {voiMake} {voiModel} vehicle{voiCount !== 1 ? 's' : ''} in stock
                  </strong>
                </ico-hyperlink>
              </div>
            )}
            <div class="ml-4 flex flex-row mb-4">
              <span class="w-6 text-center h-4">
                <svg-icon icon="map-marker" width="20" />
              </span>
              <div class="items-center">
                {isClosest && <span class="font-bold text-base">Closest to you!</span>}
                <div class="flex flex-row">
                  <span class="text-sm font-bold">{distance} miles away</span>
                  {direction && (
                    <div class="ml-8 font-bold text-sm">
                      <ico-hyperlink href={direction} target="_blank" onClick={() => trackGetDirectionClick(index)}>
                        {showDealerList
                          ? `${dealershipAddress ? `${dealershipAddress},` : ''} ${dealershipCity ? `${dealershipCity},` : ''} ${dealershipState} ${dealershipZipCode}`
                          : 'Get Directions'}
                      </ico-hyperlink>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="justify-center flex flex-col @xs:w-full @4xl:max-w-sm ml-auto">
          <div class="flex justify-end flex-col w-full @4xl:max-w-fit self-end">
            <slot name="right" />
          </div>
          {dealerInventory.length > 0 && (
            <div class="flex ml-0 @xs:flex-col @xs:justify-center @4xl:flex-row @4xl:justify-end">
              {onVehiclesDealerInventory(dealerInventory).map(({ vehicleInfo }) => (
                <inventory-listing {...vehicleInfo} vehicleImageURL={vehicleInfo.vehicleImage.url} index={index} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
}
